import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, X, Save } from "lucide-react";
import { fetchEpisodes, createEpisode, updateEpisode } from "@/lib/api";
import UploadField from "@/components/UploadField";

const EMPTY = {
    number: "",
    title: "",
    subtitle: "",
    description: "",
    guest: "",
    guest_role: "",
    guest_quote: "",
    guest_bio: "",
    poster_art: "",
    audio_url: "",
    duration: "",
    published_at: "",
    tags: "",
    spotify_url: "",
    youtube_url: "",
    show_notes: "",
    transcript: "",
};

const inputCls =
    "w-full bg-paper border-2 border-ink px-3.5 py-2.5 text-sm text-ink placeholder:text-ink/40 focus:outline-none focus:ring-2 focus:ring-pink transition-shadow duration-150";

const toPayload = (f) => ({
    ...f,
    tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
    show_notes: f.show_notes.split("\n").map((s) => s.trim()).filter(Boolean),
});

const fromEpisode = (ep) => ({
    ...EMPTY,
    ...ep,
    tags: (ep.tags || []).join(", "),
    show_notes: (ep.show_notes || []).join("\n"),
});

const Field = ({ label, k, form, set, textarea = false, placeholder = "" }) => (
    <label className="block">
        <span className="font-mono-brand text-[10px] uppercase tracking-[0.18em] text-ink/60">{label}</span>
        {textarea ? (
            <textarea
                data-testid={`episode-field-${k}`}
                className={`${inputCls} mt-1.5 min-h-[90px] resize-y`}
                placeholder={placeholder}
                value={form[k]}
                onChange={(e) => set({ ...form, [k]: e.target.value })}
            />
        ) : (
            <input
                data-testid={`episode-field-${k}`}
                className={`${inputCls} mt-1.5`}
                placeholder={placeholder}
                value={form[k]}
                onChange={(e) => set({ ...form, [k]: e.target.value })}
            />
        )}
    </label>
);

export default function EpisodeManager() {
    const [episodes, setEpisodes] = useState([]);
    const [editing, setEditing] = useState(null); // null | "new" | episode id
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    const load = () => fetchEpisodes().then(setEpisodes).catch(() => {});
    useEffect(() => {
        load();
    }, []);

    const startNew = () => {
        setForm(EMPTY);
        setEditing("new");
    };
    const startEdit = (ep) => {
        setForm(fromEpisode(ep));
        setEditing(ep.id);
    };

    const save = async (e) => {
        e.preventDefault();
        if (!form.title.trim() || !form.number.trim()) {
            toast.error("Number and title are required.");
            return;
        }
        setBusy(true);
        try {
            if (editing === "new") {
                await createEpisode(toPayload(form));
                toast.success("Episode added to the archive.");
            } else {
                await updateEpisode(editing, toPayload(form));
                toast.success("Episode updated.");
            }
            setEditing(null);
            await load();
        } catch {
            toast.error("Could not save. Please try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="episode-manager">
            <div className="flex items-center justify-between gap-4 flex-wrap">
                <p className="text-sm text-ink/70">{episodes.length} episodes in the archive</p>
                <button
                    data-testid="episode-new-btn"
                    onClick={startNew}
                    className="inline-flex items-center gap-2 bg-pink text-cream font-sub font-bold text-sm px-5 py-3 border-2 border-ink hard-shadow-sm transition-transform duration-200 hover:-translate-y-0.5"
                >
                    <Plus className="w-4 h-4" /> New episode
                </button>
            </div>

            <div className="mt-8 space-y-4">
                {episodes.map((ep) => (
                    <div
                        key={ep.id}
                        data-testid={`manager-episode-${ep.id}`}
                        className="flex items-center gap-4 bg-cream border-2 border-ink hard-shadow-sm p-4"
                    >
                        <img
                            src={ep.poster_art}
                            alt=""
                            className="w-12 h-14 object-cover object-top border-2 border-ink shrink-0"
                        />
                        <div className="min-w-0">
                            <p className="font-mono-brand text-[10px] uppercase tracking-[0.2em] text-pink">
                                EP {ep.number}
                            </p>
                            <p className="font-sub font-bold text-ink truncate">{ep.title}</p>
                            <p className="text-xs text-ink/60 truncate">{ep.guest}</p>
                        </div>
                        <button
                            data-testid={`manager-edit-${ep.id}`}
                            onClick={() => startEdit(ep)}
                            className="ml-auto inline-flex items-center gap-1.5 bg-paper text-ink font-sub font-bold text-sm px-4 py-2.5 border-2 border-ink transition-colors duration-150 hover:bg-mustard/30"
                        >
                            <Pencil className="w-3.5 h-3.5" /> Edit
                        </button>
                    </div>
                ))}
            </div>

            {editing && (
                <form
                    onSubmit={save}
                    data-testid="episode-editor-form"
                    className="mt-10 bg-paperalt border-2 border-ink hard-shadow p-6 sm:p-8"
                >
                    <div className="flex items-center justify-between gap-4">
                        <h3 className="font-display font-black text-2xl text-ink">
                            {editing === "new" ? "New episode" : "Edit episode"}
                        </h3>
                        <button
                            type="button"
                            data-testid="episode-editor-cancel"
                            onClick={() => setEditing(null)}
                            className="p-2 border-2 border-ink bg-cream text-ink hover:bg-mustard/30 transition-colors duration-150"
                            aria-label="Cancel"
                        >
                            <X className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="mt-6 grid sm:grid-cols-3 gap-4">
                        <Field label="Number *" k="number" form={form} set={setForm} placeholder="03" />
                        <Field label="Duration" k="duration" form={form} set={setForm} placeholder="52:10" />
                        <Field label="Published (YYYY-MM-DD)" k="published_at" form={form} set={setForm} />
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        <Field label="Title *" k="title" form={form} set={setForm} />
                        <Field label="Subtitle" k="subtitle" form={form} set={setForm} />
                    </div>
                    <div className="mt-4">
                        <Field label="Card description" k="description" form={form} set={setForm} textarea />
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        <Field label="Guest name" k="guest" form={form} set={setForm} />
                        <Field label="Guest role" k="guest_role" form={form} set={setForm} />
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        <UploadField
                            label="Poster art"
                            testid="episode-poster"
                            accept="image/*"
                            value={form.poster_art}
                            onChange={(v) => setForm({ ...form, poster_art: v })}
                        />
                        <UploadField
                            label="Audio file"
                            testid="episode-audio"
                            accept="audio/*"
                            value={form.audio_url}
                            onChange={(v) => setForm({ ...form, audio_url: v })}
                        />
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        <Field label="Spotify URL" k="spotify_url" form={form} set={setForm} placeholder="https://…" />
                        <Field label="YouTube URL" k="youtube_url" form={form} set={setForm} placeholder="https://…" />
                    </div>
                    <div className="mt-4">
                        <Field label="Tags (comma separated)" k="tags" form={form} set={setForm} placeholder="history, intimacy" />
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-4">
                        <Field label="Guest quote / one-liner" k="guest_quote" form={form} set={setForm} textarea />
                        <Field label="Guest bio" k="guest_bio" form={form} set={setForm} textarea />
                    </div>
                    <div className="mt-4">
                        <Field label="Show notes (one per line)" k="show_notes" form={form} set={setForm} textarea />
                    </div>
                    <div className="mt-4">
                        <Field label="Transcript" k="transcript" form={form} set={setForm} textarea />
                    </div>

                    <button
                        type="submit"
                        data-testid="episode-save-btn"
                        disabled={busy}
                        className="mt-7 inline-flex items-center gap-2 bg-ink text-paper font-sub font-bold px-7 py-3.5 border-2 border-ink hard-shadow-pink transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60"
                    >
                        <Save className="w-4 h-4" />
                        {busy ? "Saving…" : "Save episode"}
                    </button>
                </form>
            )}
        </div>
    );
}
